import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';
import { usePopoverBoundary } from '../helpers/PopoverBoundaryContext';

type PopoverContentProps = ComponentProps<typeof PopoverPrimitive.Content>;

export const PopoverContent = ({
  className,
  align = 'center',
  sideOffset = 4,
  style,
  ...props
}: PopoverContentProps) => {
  // 親 Popover が PopoverBoundaryProvider を立てている場合、内側 Popover を
  // その境界内に収めるよう Radix の collisionBoundary / collisionPadding を
  // 自動で渡す。明示的に props で指定されている場合はそちらを優先する
  // （{...props} を後から spread しているため）。
  const { boundary, collisionPadding, contentZIndex } = usePopoverBoundary();
  const hasBoundary = boundary !== null;

  // Popover の高さが boundary（または viewport）を超えないよう、Radix の
  // --radix-popover-content-available-height を max-height に当てる。
  // 内側 listbox 等は自身の max-h-64 と flex-1 / min-h-0 でこの枠内に自動収縮する。
  // 親 Provider が contentZIndex を渡している場合は z-index を上書きして既定（z-50）より前面化する
  // （FilterPopover 配下のドロップダウンがヘッダー等の背後に潜らないようにするため）。
  // consumer の style 指定があればそちらが優先される（後勝ち）。
  const popoverStyle = {
    maxHeight: 'var(--radix-popover-content-available-height)',
    ...(contentZIndex !== undefined ? { zIndex: contentZIndex } : {}),
    ...style,
  };

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        // collisionBoundary は Provider 配下時のみ Context 値を使う（boundary なし時は undefined＝viewport 基準）。
        // collisionPadding も Provider 配下時のみ 8px をフォールバック適用する。
        // boundary なしの全 PopoverContent に 8px を強制適用すると、shared/ui/Popover を使う既存の独立 Popover の
        // viewport 端でのフリップ／シフト挙動が変わって regression になるため。
        collisionBoundary={boundary ?? undefined}
        collisionPadding={hasBoundary ? (collisionPadding ?? 8) : collisionPadding}
        className={cn(
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'z-50 w-72 rounded-md bg-white p-4 text-popover-foreground shadow-md outline-none',
          'data-[state=closed]:animate-out data-[state=open]:animate-in',
          className,
        )}
        style={popoverStyle}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
};
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
